import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Activity, AlertTriangle, ArrowRight, Boxes, ClipboardList, Euro, Factory, PackageCheck,
  RefreshCw, ShoppingCart, Truck
} from 'lucide-react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import { PERMISSIONS, SALES_ORDER_STATUS_LABELS, formatDateTime, formatMoney, formatQty } from '@erp/shared';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { PageHeader, StatCard, Skeleton, ErrorState, ProgressBar } from '@/components/ui/misc';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

interface DashboardData {
  kpis: {
    open_orders: number; pending_units: number; in_production_units: number; pending_kits: number;
    shipments_today: number; critical_alerts: number | null; open_alerts: number | null; stock_value: number;
    low_stock_products: number;
  };
  pipeline: { status: string; count: number; units: number }[];
  production: { date: string; units: number }[];
  alerts: { id: string; type: string; severity: string; message: string; due_date: string | null; created_at: string; sku: string | null; product_name: string | null }[];
  activity: { id: number; action: string; entity: string; created_at: string; user_name: string | null; meta: Record<string, unknown> | null }[];
  canSeeActivity: boolean;
  canSeeAlerts: boolean;
  lowStock: { sku: string; name: string; unit_code: string; physical: number; reserved: number; min_stock: number }[];
}

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Inicio de sesión',
  'auth.logout': 'Cierre de sesión',
  'kit.scan': 'Escaneo de componente',
  'kit.scan_void': 'Escaneo anulado',
  'kit.complete': 'Kit completado',
  'stock.move': 'Movimiento de stock',
  'stock.inventory': 'Regularización de inventario',
  'shipment.created': 'Expedición creada',
  'purchase_invoice.create': 'Factura registrada',
  'purchase_invoice.validate': 'Factura validada',
  'order.create': 'Pedido creado',
  'order.confirm': 'Pedido confirmado',
  'order.release_units': 'Unidades liberadas',
  'planning.mrp_run': 'MRP ejecutado',
  'packaging.complete': 'Embalaje completado'
};

export function DashboardPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  /**
   * Cada panel enlaza solo a una pantalla que este usuario puede abrir: Compras y Almacén
   * ven el cuadro de mando pero no todos sus destinos, y un enlace a «Acceso restringido» es
   * peor que un panel sin enlace. Sin permiso, el panel queda informativo y sin flecha.
   */
  const puede = {
    orders: can(PERMISSIONS.ORDERS_READ),
    kits: can(PERMISSIONS.KITS_READ),
    logistics: can(PERMISSIONS.LOGISTICS_READ),
    stock: can(PERMISSIONS.STOCK_READ),
    planning: can(PERMISSIONS.PLANNING_READ),
    audit: can(PERMISSIONS.AUDIT_READ)
  };
  const enlace = (permitido: boolean, to: string) => (permitido ? to : undefined);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardData>('/dashboard'),
    refetchInterval: 60_000
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader title={t('dashboard.title')} description={t('dashboard.subtitle')} />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-28" />)}
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-80" /><Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return <ErrorState message={(error as Error)?.message ?? 'Error'} onRetry={() => void refetch()} />;
  }

  const k = data.kpis;

  return (
    <div>
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.subtitle')}
        badge={<Badge tone="success" className="gap-1.5"><span className="size-1.5 animate-pulse rounded-full bg-emerald-500" /> {t('kpis.live')}</Badge>}
        actions={(
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'animate-spin' : ''} /> {t('actions.refresh')}
          </Button>
        )}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard label={t('dashboard.openOrders')} value={k.open_orders} hint="Confirmados y en producción" icon={ShoppingCart} to={enlace(puede.orders, '/orders?status=open')} />
        <StatCard label={t('dashboard.pendingUnits')} value={k.pending_units} hint={`${k.pending_kits} kits pendientes`} icon={Boxes} tone="warning" to={enlace(puede.kits, '/kits?status=open')} />
        {/* Las unidades en planta (kit ya completado) no tienen lista propia todavía: sin
            enlace antes que mandar a los kits en preparación, que son otro concepto. */}
        <StatCard label={t('dashboard.inProduction')} value={k.in_production_units} hint="Unidades en planta" icon={Factory} tone="accent" />
        <StatCard label={t('dashboard.shipmentsToday')} value={k.shipments_today} hint="Expediciones del día" icon={Truck} tone="success" to={enlace(puede.logistics, '/logistics')} />
        <StatCard label={t('dashboard.stockValue')} value={formatMoney(k.stock_value)} hint={`${k.low_stock_products} artículos bajo mínimo`} icon={Euro} tone="neutral" to={enlace(puede.stock, '/stock')} />
        {data.canSeeAlerts ? (
          <StatCard
            label={t('dashboard.criticalAlerts')}
            value={k.critical_alerts ?? 0}
            hint={`${k.open_alerts ?? 0} alertas abiertas`}
            icon={AlertTriangle}
            tone={(k.critical_alerts ?? 0) > 0 ? 'danger' : 'success'}
            to={enlace(puede.planning, '/planning')}
          />
        ) : null}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t('dashboard.pipeline')}
            description={puede.orders ? 'Unidades comprometidas por estado · toca una barra para ver esos pedidos' : 'Unidades comprometidas por estado'}
            actions={puede.orders ? (
              <Link to="/orders?status=open" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                Ver pedidos de venta <ArrowRight aria-hidden />
              </Link>
            ) : undefined}
          />
          <CardContent className="h-[280px] pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.pipeline} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="status" tick={{ fontSize: 10 }} tickFormatter={(v) => SALES_ORDER_STATUS_LABELS[v as keyof typeof SALES_ORDER_STATUS_LABELS] ?? v} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', fontSize: 12 }}
                  formatter={(value, name) => [name === 'units' ? formatQty(value, 0) : value, name === 'units' ? 'Unidades' : 'Pedidos']}
                />
                <Bar
                  dataKey="units"
                  radius={[4, 4, 0, 0]}
                  fill="#2563eb"
                  minPointSize={2}
                  cursor={puede.orders ? 'pointer' : undefined}
                  // Tocar una barra lleva a los pedidos de ese estado, no a un gráfico muerto.
                  onClick={(entrada) => {
                    if (!puede.orders) return;
                    const estado = (entrada as { status?: string } | undefined)?.status;
                    if (estado) navigate(`/orders?status=${encodeURIComponent(estado)}`);
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
            {puede.orders && data.pipeline.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5 px-2 pb-2">
                {data.pipeline.map((row) => (
                  <li key={row.status}>
                    <Link
                      to={`/orders?status=${encodeURIComponent(row.status)}`}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] transition hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {SALES_ORDER_STATUS_LABELS[row.status as keyof typeof SALES_ORDER_STATUS_LABELS] ?? row.status}
                      <span className="tabular-nums text-muted-foreground">{row.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('dashboard.production')}
            description={t('kpis.days14')}
            actions={puede.kits ? (
              <Link to="/kits?status=open" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                Ver kits <ArrowRight aria-hidden />
              </Link>
            ) : undefined}
          />
          <CardContent className="h-[280px] pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.production} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="productionFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0d9488" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(5)} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', fontSize: 12 }} />
                <Area type="monotone" dataKey="units" stroke="#0d9488" strokeWidth={2} fill="url(#productionFill)" name="Unidades" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className={data.canSeeAlerts && data.canSeeActivity
        ? 'mt-4 grid gap-4 xl:grid-cols-[1.4fr_1fr]'
        : 'mt-4 grid gap-4'}>
        {data.canSeeAlerts ? (
        <Card>
          <CardHeader
            title={t('dashboard.alerts')}
            description="Déficits y stock por debajo de mínimo"
            actions={puede.planning ? (
              <Link to="/planning" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                Ver todo <ArrowRight aria-hidden />
              </Link>
            ) : undefined}
          />
          <div className="divide-y divide-border">
            {data.alerts.length === 0 ? (
              <div className="flex items-center gap-2 px-4 py-10 text-sm text-muted-foreground">
                <PackageCheck className="size-4 text-emerald-500" /> Sin alertas abiertas. Todo bajo control.
              </div>
            ) : data.alerts.map((alert) => {
              const destino = alert.sku && puede.stock
                ? `/stock?q=${encodeURIComponent(alert.sku)}`
                : puede.planning ? '/planning' : null;
              const contenido = (
                <>
                  <span className={`mt-1.5 size-2 shrink-0 rounded-full ${alert.severity === 'critical' ? 'bg-red-500' : alert.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={alert.severity} />
                      {alert.sku ? <span className="font-mono text-[10px] text-muted-foreground">{alert.sku}</span> : null}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-foreground">{alert.message}</p>
                  </div>
                  {destino ? <ArrowRight className="mt-2 size-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
                </>
              );
              return destino ? (
                <Link
                  key={alert.id}
                  to={destino}
                  className="flex items-start gap-3 px-4 py-3 transition hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {contenido}
                </Link>
              ) : (
                <div key={alert.id} className="flex items-start gap-3 px-4 py-3">{contenido}</div>
              );
            })}

          </div>
        </Card>
        ) : null}

        {data.canSeeActivity ? (
        <Card>
          <CardHeader
            title={t('dashboard.activity')}
            description="Auditoría en tiempo real"
            actions={puede.audit ? (
              <Link to="/admin?tab=audit" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                Ver auditoría <ArrowRight aria-hidden />
              </Link>
            ) : undefined}
          />
          <div className="divide-y divide-border">
            {data.activity.length === 0 ? (
              <p className="px-4 py-10 text-xs text-muted-foreground">Sin actividad reciente</p>
            ) : data.activity.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Activity className="size-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">{ACTION_LABELS[entry.action] ?? entry.action}</p>
                  <p className="truncate text-[10px] text-muted-foreground">{entry.user_name ?? 'Sistema'} · {formatDateTime(entry.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
        ) : null}
      </div>

      <Card className="mt-4">
        <CardHeader title={t('dashboard.lowStock')} description="Disponible por debajo del mínimo configurado" actions={<Link to="/stock?lowStock=1"><Button variant="ghost" size="sm">Ver stock <ArrowRight /></Button></Link>} />
        <div className="overflow-x-auto">
          <table className="table-dense w-full">
            <thead className="border-b border-border bg-muted/30">
              <tr><th>Artículo</th><th>Disponible</th><th>Mínimo</th><th className="w-48">Cobertura</th></tr>
            </thead>
            <tbody>
              {data.lowStock.length === 0 ? (
                <tr><td colSpan={4} className="py-8 text-center text-xs text-muted-foreground">Sin artículos bajo mínimo</td></tr>
              ) : data.lowStock.map((item) => {
                const available = item.physical - item.reserved;
                const ratio = item.min_stock > 0 ? Math.min(100, Math.max(0, (available / item.min_stock) * 100)) : 0;
                return (
                  <tr key={item.sku}>
                    <td>
                      <p className="font-medium">{item.name}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{item.sku}</p>
                    </td>
                    <td className={available <= 0 ? 'font-semibold text-red-600' : 'font-medium'}>{formatQty(available)} {item.unit_code}</td>
                    <td className="text-muted-foreground">{formatQty(item.min_stock)} {item.unit_code}</td>
                    <td><ProgressBar value={ratio} tone={ratio < 50 ? 'warning' : 'primary'} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[
          { to: '/kits', label: 'Preparar kits pendientes', icon: ClipboardList },
          { to: '/packaging', label: 'Puesto de embalaje', icon: PackageCheck },
          { to: '/logistics', label: 'Expedir unidades listas', icon: Truck }
        ].map((action) => (
          <Link key={action.to} to={action.to} className="panel flex items-center justify-between gap-3 px-4 py-3 text-sm transition hover:border-primary/40 hover:shadow-lift">
            <span className="flex items-center gap-2 font-medium"><action.icon className="size-4 text-primary" /> {action.label}</span>
            <ArrowRight className="size-4 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </div>
  );
}
